import {
  Attachment,
  AttachmentUsage,
  AUTH_TOKEN_HEADER,
  AuthStatus,
  type BoardInput,
  CalendarEvent,
  CalendarFeed,
  CalendarFeedList,
  CalendarItems,
  type CreateFeed,
  type CreateNote,
  type CreateNotebook,
  type CreateTodo,
  type CreateWidget,
  CSRF_HEADER,
  CsrfTokenResponse,
  DeletedAttachments,
  ErrorResponse,
  type EventInput,
  HealthResponse,
  HomeBoard,
  HomeContainerStats,
  HomeContainers,
  HomeDashboard,
  HomeWidget,
  IconSearchResults,
  type ItemsQuery,
  type LoginRequest,
  Note,
  Notebook,
  NotebookList,
  NoteList,
  type NotesQuery,
  NotesSync,
  type NotesSyncQuery,
  NoteTitles,
  routes,
  SearchResults,
  ServerStats,
  Settings,
  type SetupRequest,
  SignInResponse,
  Todo,
  TodoList,
  TrashList,
  type UpdateFeed,
  type UpdateNote,
  type UpdateNotebook,
  type UpdateSettings,
  type UpdateTodo,
  type UpdateWidget,
  User,
} from "@ronin/contracts";
import { z } from "zod";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** spec11 FR14: the server could not be reached (offline, DNS, timeout); no status code. */
export const NETWORK_ERROR = "network";

export const isNetworkError = (error: unknown): error is ApiError =>
  error instanceof ApiError && error.code === NETWORK_ERROR;

const networkError = () => new ApiError(0, NETWORK_ERROR, "No connection");

/** spec08: where the native app keeps its session token (secure storage). */
export interface TokenStore {
  get: () => Promise<string | null>;
  set: (token: string | null) => Promise<void>;
}

export interface ApiClientOptions {
  /**
   * Origin of the API, e.g. "https://ronin.example.com". Empty for same-origin (web). A
   * function is read on every request (the app's server address can change).
   */
  baseUrl?: string | (() => string);
  /**
   * spec08: bearer auth for the native app. Requests send the token instead of cookies and
   * need no CSRF token; sign-in stores the token the server returns, sign-out deletes it.
   */
  tokens?: TokenStore;
  fetch?: typeof globalThis.fetch;
  /**
   * spec11 FR14: false while the device knows it is offline; requests then fail at once with
   * a network error instead of waiting for a timeout.
   */
  online?: () => boolean;
  /** Uploads use XMLHttpRequest for progress events (fetch has none); tests pass a fake. */
  xhr?: () => XMLHttpRequest;
}

export interface UploadOptions {
  /** Share of the file sent so far, 0 to 1. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

const NoContent = z.undefined();

const SignOutResponse = z.object({ success: z.boolean() });

/** Better Auth's own error shape (for /api/auth/* routes). */
const BetterAuthError = z.object({ message: z.string(), code: z.string().optional() });

export function createApiClient(options: ApiClientOptions = {}) {
  const base = options.baseUrl ?? "";
  const url = (path: string) => `${typeof base === "function" ? base() : base}${path}`;
  const rawFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const online = options.online ?? (() => true);
  /** Failures to reach the server become a network ApiError; an abort stays an abort. */
  const doFetch: typeof globalThis.fetch = async (input, init) => {
    if (!online()) throw networkError();
    try {
      return await rawFetch(input, init);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      throw networkError();
    }
  };
  const newXhr = options.xhr ?? (() => new XMLHttpRequest());
  const tokens = options.tokens;
  let csrfToken: string | undefined;

  /** Credentials for one request: the bearer token (app) or the cookie (web). */
  async function authInit(headers: Record<string, string>): Promise<RequestCredentials> {
    if (!tokens) return "include";
    const token = await tokens.get();
    if (token) headers.authorization = `Bearer ${token}`;
    return "omit";
  }

  async function send(method: Method, path: string, body: unknown) {
    const headers: Record<string, string> = { accept: "application/json" };
    const credentials = await authInit(headers);
    if (method === "GET") return doFetch(url(path), { method, headers, credentials });
    headers["content-type"] = "application/json";
    if (!tokens) headers[CSRF_HEADER] = await getCsrfToken();
    return doFetch(url(path), {
      method,
      headers,
      credentials,
      body: JSON.stringify(body ?? {}),
    });
  }

  async function getCsrfToken(refresh = false): Promise<string> {
    if (!csrfToken || refresh) {
      const res = await doFetch(url(routes.csrf), { credentials: "include" });
      csrfToken = (await parse(res, CsrfTokenResponse)).token;
    }
    return csrfToken;
  }

  async function request<T extends z.ZodType>(
    method: Method,
    path: string,
    schema: T,
    body?: unknown,
  ): Promise<z.infer<T>> {
    let res = await send(method, path, body);
    // The CSRF secret rotates with the session; fetch a fresh token once and retry.
    if (res.status === 403 && method !== "GET" && !tokens) {
      await getCsrfToken(true);
      res = await send(method, path, body);
    }
    return parse(res, schema);
  }

  /** POSTs `file` as multipart form data; resolves with the raw response. */
  function sendFile(file: Blob, name: string, auth: Record<string, string>, opts: UploadOptions) {
    return new Promise<Response>((resolve, reject) => {
      const xhr = newXhr();
      xhr.open("POST", url(routes.attachments));
      xhr.withCredentials = !tokens;
      xhr.setRequestHeader("accept", "application/json");
      for (const [key, value] of Object.entries(auth)) xhr.setRequestHeader(key, value);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) opts.onProgress?.(event.loaded / event.total);
      };
      xhr.onload = () => resolve(new Response(xhr.responseText || null, { status: xhr.status }));
      xhr.onerror = () => reject(networkError());
      xhr.onabort = () => reject(new DOMException("Upload cancelled", "AbortError"));
      opts.signal?.addEventListener("abort", () => xhr.abort(), { once: true });
      const form = new FormData();
      form.append("file", file, name);
      xhr.send(form);
    });
  }

  async function upload(file: Blob, name: string, opts: UploadOptions = {}) {
    if (tokens) {
      const auth: Record<string, string> = {};
      await authInit(auth);
      return parse(await sendFile(file, name, auth, opts), Attachment);
    }
    let res = await sendFile(file, name, { [CSRF_HEADER]: await getCsrfToken() }, opts);
    if (res.status === 403) {
      res = await sendFile(file, name, { [CSRF_HEADER]: await getCsrfToken(true) }, opts);
    }
    return parse(res, Attachment);
  }

  /** An attachment file (`/api/attachments/<id>`) with the client's credentials. */
  async function file(path: string, init: { method?: "GET" | "HEAD"; signal?: AbortSignal } = {}) {
    const headers: Record<string, string> = {};
    const credentials = await authInit(headers);
    return doFetch(url(path), { ...init, headers, credentials });
  }

  async function signIn(input: LoginRequest) {
    const res = await send("POST", routes.auth.signIn, input);
    const { user } = await parse(res, SignInResponse);
    if (tokens) {
      const token = res.headers.get(AUTH_TOKEN_HEADER);
      if (!token) throw new ApiError(res.status, "internal", "The server sent no session token");
      await tokens.set(token);
    }
    return user;
  }

  async function signOut() {
    try {
      await request("POST", routes.auth.signOut, SignOutResponse);
    } finally {
      // spec08 FR8: the app forgets its token even when the server cannot be reached.
      await tokens?.set(null);
    }
  }

  return {
    health: () => request("GET", routes.health, HealthResponse),
    auth: {
      /** Setup state and the signed-in user (null when signed out). */
      session: () => request("GET", routes.session, AuthStatus),
      setup: (input: SetupRequest) => request("POST", routes.setup, User, input),
      signIn,
      signOut,
    },
    todos: {
      list: async () => (await request("GET", routes.todos, TodoList)).todos,
      create: (input: CreateTodo) => request("POST", routes.todos, Todo, input),
      update: (id: string, patch: UpdateTodo) => request("PATCH", routes.todo(id), Todo, patch),
      remove: (id: string) => request("DELETE", routes.todo(id), NoContent),
      restore: (id: string) => request("POST", routes.restoreTodo(id), Todo),
    },
    calendar: {
      items: async ({ from, to }: ItemsQuery) =>
        (
          await request(
            "GET",
            `${routes.calendarItems}?${new URLSearchParams({ from, to })}`,
            CalendarItems,
          )
        ).items,
      createEvent: (input: EventInput) =>
        request("POST", routes.calendarEvents, CalendarEvent, input),
      updateEvent: (id: string, input: EventInput) =>
        request("PATCH", routes.calendarEvent(id), CalendarEvent, input),
      removeEvent: (id: string) => request("DELETE", routes.calendarEvent(id), NoContent),
      restoreEvent: (id: string) => request("POST", routes.restoreCalendarEvent(id), CalendarEvent),
      feeds: async () => (await request("GET", routes.calendarFeeds, CalendarFeedList)).feeds,
      createFeed: (input: CreateFeed) => request("POST", routes.calendarFeeds, CalendarFeed, input),
      updateFeed: (id: string, patch: UpdateFeed) =>
        request("PATCH", routes.calendarFeed(id), CalendarFeed, patch),
      removeFeed: (id: string) => request("DELETE", routes.calendarFeed(id), NoContent),
      restoreFeed: (id: string) => request("POST", routes.restoreCalendarFeed(id), CalendarFeed),
      refreshFeed: (id: string) => request("POST", routes.refreshCalendarFeed(id), CalendarFeed),
    },
    notes: {
      notebooks: async () => (await request("GET", routes.notebooks, NotebookList)).notebooks,
      createNotebook: (input: CreateNotebook) => request("POST", routes.notebooks, Notebook, input),
      updateNotebook: (id: string, patch: UpdateNotebook) =>
        request("PATCH", routes.notebook(id), Notebook, patch),
      removeNotebook: (id: string) => request("DELETE", routes.notebook(id), NoContent),
      restoreNotebook: (id: string) => request("POST", routes.restoreNotebook(id), Notebook),
      list: async (query: NotesQuery = {}) => {
        const params = new URLSearchParams(
          Object.entries(query).filter((e): e is [string, string] => e[1] !== undefined),
        );
        const qs = params.size > 0 ? `?${params}` : "";
        return (await request("GET", `${routes.notes}${qs}`, NoteList)).notes;
      },
      get: (id: string) => request("GET", routes.note(id), Note),
      create: (input: CreateNote) => request("POST", routes.notes, Note, input),
      update: (id: string, patch: UpdateNote) => request("PATCH", routes.note(id), Note, patch),
      remove: (id: string) => request("DELETE", routes.note(id), NoContent),
      restore: (id: string) => request("POST", routes.restoreNote(id), Note),
      /** spec11: one page of changes for the app's offline copy. */
      sync: (query: NotesSyncQuery = {}) => {
        const params = new URLSearchParams(
          Object.entries(query).filter((e): e is [string, string] => e[1] !== undefined),
        );
        const qs = params.size > 0 ? `?${params}` : "";
        return request("GET", `${routes.notesSync}${qs}`, NotesSync);
      },
      search: async (q: string, notebook?: string) => {
        const params = new URLSearchParams({ q, ...(notebook && { notebook }) });
        return (await request("GET", `${routes.searchNotes}?${params}`, SearchResults)).results;
      },
      titles: async (prefix: string) =>
        (
          await request(
            "GET",
            `${routes.noteTitles}?${new URLSearchParams({ prefix })}`,
            NoteTitles,
          )
        ).titles,
      trash: async () => (await request("GET", routes.trash, TrashList)).items,
      destroy: (kind: "note" | "notebook", id: string) =>
        request("DELETE", routes.trashItem(kind, id), NoContent),
      emptyTrash: () => request("DELETE", routes.trash, NoContent),
    },
    attachments: {
      /** spec05 FR1: one file; the server decides the type from its content. */
      upload,
      file,
      usage: () => request("GET", routes.attachmentUsage, AttachmentUsage),
      deleteUnused: () => request("DELETE", routes.unusedAttachments, DeletedAttachments),
    },
    /** spec12: the home dashboard. */
    home: {
      get: () => request("GET", routes.home, HomeDashboard),
      createBoard: (input: BoardInput) => request("POST", routes.homeBoards, HomeBoard, input),
      renameBoard: (id: string, input: BoardInput) =>
        request("PATCH", routes.homeBoard(id), HomeBoard, input),
      removeBoard: (id: string) => request("DELETE", routes.homeBoard(id), NoContent),
      restoreBoard: (id: string) => request("POST", routes.restoreHomeBoard(id), HomeBoard),
      orderBoards: (ids: string[]) => request("PUT", routes.homeBoardOrder, NoContent, { ids }),
      createWidget: (boardId: string, input: CreateWidget) =>
        request("POST", routes.homeWidgets(boardId), HomeWidget, input),
      updateWidget: (id: string, patch: UpdateWidget) =>
        request("PATCH", routes.homeWidget(id), HomeWidget, patch),
      removeWidget: (id: string) => request("DELETE", routes.homeWidget(id), NoContent),
      restoreWidget: (id: string) => request("POST", routes.restoreHomeWidget(id), HomeWidget),
      /** spec12 FR11: names from the service icon set. */
      searchIcons: async (q: string) =>
        (
          await request(
            "GET",
            `${routes.homeIcons}?${new URLSearchParams({ q })}`,
            IconSearchResults,
          )
        ).icons,
      /** Copies one service icon into the user's attachments. */
      importIcon: (name: string) => request("POST", routes.homeIcon(name), Attachment),
      /** spec14: this server's containers and stats (404 when not set up). */
      containers: async () =>
        (await request("GET", routes.homeContainers, HomeContainers)).containers,
      serverStats: () => request("GET", routes.homeServerStats, ServerStats),
      /** spec14 FR7a: stats of the containers whose cards show resource boxes. */
      containerStats: async (names: string[]) =>
        (
          await request(
            "GET",
            `${routes.homeContainerStats}?${new URLSearchParams({ names: names.join(",") })}`,
            HomeContainerStats,
          )
        ).stats,
    },
    settings: {
      get: () => request("GET", routes.settings, Settings),
      update: (patch: UpdateSettings) => request("PATCH", routes.settings, Settings, patch),
      testNotification: () => request("POST", routes.testNotification, NoContent),
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

async function parse<T extends z.ZodType>(res: Response, schema: T): Promise<z.infer<T>> {
  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    json = undefined;
  }
  if (!res.ok) {
    const error = ErrorResponse.safeParse(json);
    if (error.success) {
      throw new ApiError(res.status, error.data.error.code, error.data.error.message);
    }
    const authError = BetterAuthError.safeParse(json);
    if (authError.success) {
      throw new ApiError(res.status, authError.data.code ?? "bad_request", authError.data.message);
    }
    throw new ApiError(res.status, "internal", `Request failed with status ${res.status}`);
  }
  return schema.parse(json);
}
